import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiUrl } from '../config/api';

const fmtQty = (value) => Number(value || 0).toLocaleString('ru-RU', { minimumFractionDigits: 0, maximumFractionDigits: 3 });

export default function WarehousePage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [filters, setFilters] = useState({
    code: '',
    name: '',
    address: '',
    customer: '',
    activePositions: '',
    totalPositions: '',
    manualPositions: '',
  });

  useEffect(() => {
    document.title = 'Склад';
  }, []);

  const load = () => {
    setLoading(true);
    setError('');
    fetch(apiUrl('/api/warehouse'))
      .then(async (r) => {
        const data = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(data?.error || 'warehouse_load_failed');
        return data;
      })
      .then((data) => {
        const rows = Array.isArray(data?.items) ? data.items.slice() : [];
        if (rows.length) {
          rows.sort((a, b) => Number(a.codeBase) - Number(b.codeBase));
          setItems(rows);
          setLoading(false);
          return;
        }
        return fetch(apiUrl('/api/objects'))
          .then(async (r) => {
            const fallbackData = await r.json().catch(() => ([]));
            if (!r.ok) throw new Error('objects_fallback_failed');
            const objectRows = Array.isArray(fallbackData) ? fallbackData : [];
            objectRows.sort((a, b) => Number(a.codeBase) - Number(b.codeBase));
            setItems(objectRows.map((item) => ({
              ...item,
              totalPositions: 0,
              activePositions: 0,
              writtenOffPositions: 0,
              manualPositions: 0,
              activeQuantity: 0,
            })));
          });
      })
      .then(() => {
        setLoading(false);
      })
      .catch(() => {
        setError('Ошибка загрузки склада');
        setLoading(false);
      });
  };

  useEffect(() => {
    load();
  }, []);

  const onFilterChange = (key, value) => setFilters((prev) => ({ ...prev, [key]: value }));
  const fill = (key, value, event) => {
    if (event && event.ctrlKey) {
      event.stopPropagation();
      setFilters((prev) => ({ ...prev, [key]: String(value ?? '') }));
      return true;
    }
    return false;
  };
  const openObject = (id) => {
    window.open(`/warehouse/${encodeURIComponent(id)}`, '_blank', 'noopener,noreferrer');
  };

  const filtered = useMemo(() => {
    const normalized = Object.fromEntries(
      Object.entries(filters).map(([key, value]) => [key, String(value || '').toLowerCase()])
    );
    return items.filter((item) => (
      (normalized.code === '' || String(item.codeBase || '').toLowerCase().includes(normalized.code)) &&
      (normalized.name === '' || String(item.name || '').toLowerCase().includes(normalized.name)) &&
      (normalized.address === '' || String(item.address || '').toLowerCase().includes(normalized.address)) &&
      (normalized.customer === '' || String(item.customer || '').toLowerCase().includes(normalized.customer)) &&
      (normalized.activePositions === '' || String(item.activePositions || '').toLowerCase().includes(normalized.activePositions)) &&
      (normalized.totalPositions === '' || String(item.totalPositions || '').toLowerCase().includes(normalized.totalPositions)) &&
      (normalized.manualPositions === '' || String(item.manualPositions || '').toLowerCase().includes(normalized.manualPositions))
    ));
  }, [items, filters]);

  const journalWrapperStyle = {
    background: 'var(--bg-card, #fff)',
    borderRadius: 20,
    border: '1px solid var(--border-200, #e6e8ef)',
    boxShadow: 'var(--shadow-1, 0 22px 44px rgba(15,23,42,0.08))',
    overflowX: 'auto',
  };
  const journalTableStyle = {
    borderCollapse: 'separate',
    borderSpacing: 0,
    width: '100%',
    tableLayout: 'fixed',
    borderRadius: 18,
    overflow: 'hidden',
    fontSize: 15,
  };
  const thBase = {
    border: '1px solid var(--border-200, #e6e8ef)',
    padding: '10px 12px',
    background: 'linear-gradient(135deg, var(--brand-50, #f5efff), #f3f4f8)',
    fontWeight: 700,
    textAlign: 'left',
    color: 'var(--text-700, #2b2f36)',
    whiteSpace: 'normal',
  };
  const filterTh = { ...thBase, fontWeight: 500, background: '#fdfdff' };
  const tdBase = {
    border: '1px solid var(--border-200, #e6e8ef)',
    padding: '10px 12px',
    background: '#fff',
    textAlign: 'left',
    whiteSpace: 'normal',
    wordBreak: 'break-word',
  };
  const filterInputStyle = {
    width: '100%',
    padding: '6px 8px',
    fontSize: 13,
    borderRadius: 8,
    border: '1px solid #cfd4e1',
    boxSizing: 'border-box',
    minWidth: 0,
  };
  const rowBaseStyle = { transition: 'background 0.18s ease, box-shadow 0.18s ease', cursor: 'pointer' };
  const selectedRowExtras = {
    background: 'rgba(86,61,255,0.12)',
    boxShadow: 'inset 0 0 0 2px rgba(86,61,255,0.35)',
    outline: '2px solid rgba(86,61,255,0.35)',
    outlineOffset: -2,
  };
  const journalLinkStyle = { color: '#1d4ed8', textDecoration: 'underline', fontWeight: 600 };

  return (
    <div style={{ padding: '32px 0 0 0' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
        <Link to="/">&larr; В меню</Link>
        <h2 style={{ margin: 0 }}>Склад</h2>
        <div style={{ marginLeft: 'auto' }}>
          <button onClick={load} style={{ padding: '8px 12px' }}>Обновить</button>
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center' }}>Загрузка…</div>
      ) : error ? (
        <div style={{ textAlign: 'center', color: '#c00' }}>{error}</div>
      ) : (
        <>
          <div style={journalWrapperStyle} className="journal-card">
            <table style={journalTableStyle} className="journal-table">
              <thead>
                <tr>
                  <th style={{ ...thBase, width: '8%' }}>Код</th>
                  <th style={{ ...thBase, width: '24%' }}>Объект</th>
                  <th style={{ ...thBase, width: '22%' }}>Адрес</th>
                  <th style={{ ...thBase, width: '16%' }}>Заказчик</th>
                  <th style={{ ...thBase, width: '10%' }}>В остатке</th>
                  <th style={{ ...thBase, width: '10%' }}>Всего позиций</th>
                  <th style={{ ...thBase, width: '10%' }}>Ручных</th>
                </tr>
                <tr>
                  <th style={filterTh}><input value={filters.code} onChange={(e) => onFilterChange('code', e.target.value)} placeholder="Поиск..." style={filterInputStyle} /></th>
                  <th style={filterTh}><input value={filters.name} onChange={(e) => onFilterChange('name', e.target.value)} placeholder="Поиск..." style={filterInputStyle} /></th>
                  <th style={filterTh}><input value={filters.address} onChange={(e) => onFilterChange('address', e.target.value)} placeholder="Поиск..." style={filterInputStyle} /></th>
                  <th style={filterTh}><input value={filters.customer} onChange={(e) => onFilterChange('customer', e.target.value)} placeholder="Поиск..." style={filterInputStyle} /></th>
                  <th style={filterTh}><input value={filters.activePositions} onChange={(e) => onFilterChange('activePositions', e.target.value)} placeholder="Поиск..." style={filterInputStyle} /></th>
                  <th style={filterTh}><input value={filters.totalPositions} onChange={(e) => onFilterChange('totalPositions', e.target.value)} placeholder="Поиск..." style={filterInputStyle} /></th>
                  <th style={filterTh}><input value={filters.manualPositions} onChange={(e) => onFilterChange('manualPositions', e.target.value)} placeholder="Поиск..." style={filterInputStyle} /></th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr>
                    <td colSpan={7} style={{ ...tdBase, textAlign: 'center', color: '#777' }}>Объектов нет</td>
                  </tr>
                ) : filtered.map((item) => (
                  <tr
                    key={item.id}
                    onClick={() => setSelectedId(item.id)}
                    onDoubleClick={() => openObject(item.id)}
                    style={{
                      ...rowBaseStyle,
                      background: selectedId === item.id ? 'rgba(86,61,255,0.08)' : 'transparent',
                      ...(selectedId === item.id ? selectedRowExtras : {}),
                    }}
                  >
                    <td style={tdBase} onClick={(e) => fill('code', item.codeBase, e)}>
                      <a href={`/warehouse/${encodeURIComponent(item.id)}`} target="_blank" rel="noreferrer" style={journalLinkStyle}>
                        {item.codeBase}
                      </a>
                    </td>
                    <td style={tdBase} onClick={(e) => fill('name', item.name, e)}>{item.name}</td>
                    <td style={tdBase} onClick={(e) => fill('address', item.address || '—', e)}>{item.address || '—'}</td>
                    <td style={tdBase} onClick={(e) => fill('customer', item.customer || '—', e)}>{item.customer || '—'}</td>
                    <td style={tdBase} onClick={(e) => fill('activePositions', item.activePositions, e)}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <strong>{item.activePositions}</strong>
                        <span style={{ fontSize: 12, color: '#64748b' }}>{fmtQty(item.activeQuantity)} шт.</span>
                      </div>
                    </td>
                    <td style={tdBase} onClick={(e) => fill('totalPositions', item.totalPositions, e)}>{item.totalPositions}</td>
                    <td style={tdBase} onClick={(e) => fill('manualPositions', item.manualPositions, e)}>{item.manualPositions}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{ marginTop: 10, fontSize: 13, color: '#475569', textAlign: 'right' }}>
            Строк: <strong>{filtered.length}</strong>
          </div>
        </>
      )}
    </div>
  );
}